import type { Cents } from "./money";

/**
 * PROJECT SALE / DISPOSITION (Phase 12, spec §41B).
 *
 * The closing statement, not a ledger: one record per project holding the
 * sale price and the deductions that produce net proceeds. Every dollar the
 * project SPENT still lives in the accounting records; this record never
 * becomes a second source of actual money.
 *
 * Closeout is reversible — a project can be reopened for a correction, and
 * the record keeps both timestamps.
 */
export interface ProjectSale {
  projectId: string;
  saleDate: string | null;
  grossSalePriceCents: Cents;
  commissionCents: Cents;
  sellerClosingCents: Cents;
  concessionsCents: Cents;
  otherSellingCents: Cents;
  /**
   * True when those selling costs are ALSO recorded as expenses in the
   * selling bucket. Accounting is then the authority and these figures are
   * documentation — the app never counts them twice.
   */
  costsRecordedInAccounting: boolean;
  notes: string;
  closedAt: string | null;
  closedBy: string | null;
  reopenedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectSaleInput {
  saleDate: string | null;
  grossSalePriceCents: Cents;
  commissionCents: Cents;
  sellerClosingCents: Cents;
  concessionsCents: Cents;
  otherSellingCents: Cents;
  costsRecordedInAccounting: boolean;
  notes: string;
}

export function validateSaleInput(input: ProjectSaleInput): string[] {
  const problems: string[] = [];
  const nonNegative = (value: Cents, name: string) => {
    if (!Number.isSafeInteger(value) || value < 0) problems.push(`${name} must be a valid non-negative amount.`);
  };
  nonNegative(input.grossSalePriceCents, "Sale price");
  nonNegative(input.commissionCents, "Commission");
  nonNegative(input.sellerClosingCents, "Seller closing costs");
  nonNegative(input.concessionsCents, "Concessions");
  nonNegative(input.otherSellingCents, "Other selling costs");
  if (input.saleDate !== null && input.saleDate.trim() === "") {
    problems.push("Enter a sale date or leave it empty.");
  }
  return problems;
}

/** A closeout item that needs attention before the numbers are final. */
export interface CloseoutWarning {
  id: string;
  label: string;
  detail: string;
  /** Where the user goes to resolve it. */
  where: "reconciliation" | "accounting" | "scope" | "materials" | "receipts";
  count: number;
}
